/**
 * @fileoverview Hook to fetch tags with their occurrence counts from note-tag
 * relationships, suitable for tag management and analytics.
 */

import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api';
import type { Tag, NoteTag } from '@/api';
import type { TagWithOccurrence } from '../types';

// Re-export type for consumers
export type { TagWithOccurrence };

/** Type guard to check if a tag has a valid ID */
function hasValidId(tag: Tag): tag is Tag & { id: string } {
  return typeof tag.id === 'string' && tag.id.length > 0;
}

/** Type guard to check if a tag is active (not deleted) with valid ID */
function isActiveTagWithId(tag: Tag): tag is Tag & { id: string } {
  return !tag.deleted_at && hasValidId(tag);
}

/** Build a map of tag IDs to occurrence counts from note-tag relationships */
function buildOccurrenceMap(noteTags: readonly NoteTag[]): Map<string, number> {
  const occurrenceMap = new Map<string, number>();
  
  for (const noteTag of noteTags) {
    if (noteTag.tag_id) {
      const currentCount = occurrenceMap.get(noteTag.tag_id) ?? 0;
      occurrenceMap.set(noteTag.tag_id, currentCount + 1);
    }
  }
  
  return occurrenceMap;
}

/** Transform a tag with ID to TagWithOccurrence */
function toTagWithOccurrence(
  tag: Tag & { id: string },
  occurrenceMap: Map<string, number>
): TagWithOccurrence {
  return {
    ...tag,
    id: tag.id,
    occurrence: occurrenceMap.get(tag.id) ?? 0,
  };
}

/**
 * Fetches all tags with their occurrence counts (how many notes use each tag).
 * Calculates occurrences from note-tag relationships.
 */
export function useTagsWithOccurrence(): UseQueryResult<TagWithOccurrence[]> {
  return useQuery({
    queryKey: ['tags', 'with-occurrence'],
    queryFn: async (): Promise<TagWithOccurrence[]> => {
      // Fetch tags and note-tags in parallel
      const [tagsResponse, noteTagsResponse] = await Promise.all([
        apiClient.getMany<Tag>('tag', {
          page: '0',
          pageSize: '1000',
        }),
        apiClient.getMany<NoteTag>('note_tag', {
          page: '0',
          pageSize: '10000', // Higher limit to capture all relationships
        }),
      ]);

      // Count occurrences for each tag
      const occurrenceMap = buildOccurrenceMap(noteTagsResponse.items);

      // Map tags to include occurrence count (only active tags with valid IDs)
      return tagsResponse.items
        .filter(isActiveTagWithId)
        .map((tag) => toTagWithOccurrence(tag, occurrenceMap));
    },
    staleTime: 3 * 60 * 1000, // 3 minutes - tags don't change very frequently
    refetchOnWindowFocus: true,
  });
}

